import { FC, Fragment } from 'react';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import CellValue from '@app/shared/components/cell-value/CellValue';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import { useLocale } from '@app/core/hooks/useLocale';
import { getWaterfallCaseYearInstrumentTrancheValue } from '@app/core/store/pwerm2-calculation-slice-selectors';
import { Pwerm2CalculationResultsDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { Operator } from '@app/shared/models/contracts/enums/shared-enums';
import { numberValueFormatter } from '@app/shared/formatters/number-value-formatter';

interface Pwerm2WaterfallToeHurdleRowsProps {
  results: Pwerm2CalculationResultsDto;
  instrumentId: string;
  trancheId: string;
  toeConditions: Array<{
    operator: keyof typeof Operator;
    testValue: number;
  }>;
  hasToeDividends: boolean;
  hasToeBuybacks: boolean;
}

export const Pwerm2WaterfallToeHurdleRows: FC<Pwerm2WaterfallToeHurdleRowsProps> = ({
  results,
  instrumentId,
  trancheId,
  toeConditions,
  hasToeDividends,
  hasToeBuybacks,
}) => {
  const { l } = useLocale();
  const hasInterimDeductions = hasToeDividends || hasToeBuybacks;

  return (
    <>
      {toeConditions.map((condition, conditionIndex) => {
        const formattedValue = numberValueFormatter({
          value: Number(condition.testValue),
          ...amountRules,
        });
        const conditionTitle = l('_ToeConditionPlaceholders', {
          operator: Operator[condition.operator],
          value: formattedValue,
          interpolation: { escapeValue: false },
        });
        const netConditionTitle = l('_NetOeHurdleConditionPlaceholders', {
          operator: Operator[condition.operator],
          value: formattedValue,
          interpolation: { escapeValue: false },
        });

        return (
          <Fragment key={conditionIndex}>
            <TableSectionValuesPerForecast
              renderSpacer={false}
              title={conditionTitle}
              renderCell={({ caseItem, forecast }) => (
                <CellValue
                  {...amountRules}
                  value={getWaterfallCaseYearInstrumentTrancheValue(
                    results,
                    caseItem.caseId,
                    forecast.forecastId,
                    instrumentId,
                    trancheId,
                    (tranche) =>
                      tranche.ordinaryEquityMetrics?.conditions[conditionIndex]?.toeHurdleDetails
                        ?.initialHurdle ?? 0
                  )}
                  dataTestId={`toe-condition-cell-${instrumentId}-${trancheId}-${conditionIndex}-${caseItem.caseId}-${forecast.forecastId}`}
                />
              )}
            />
            {hasToeBuybacks && (
              <TableSectionValuesPerForecast
                renderSpacer={false}
                title={l('_LessCumulativeBuybacks')}
                renderCell={({ caseItem, forecast }) => (
                  <CellValue
                    {...amountRules}
                    value={getWaterfallCaseYearInstrumentTrancheValue(
                      results,
                      caseItem.caseId,
                      forecast.forecastId,
                      instrumentId,
                      trancheId,
                      (tranche) =>
                        tranche.ordinaryEquityMetrics?.conditions[conditionIndex]?.toeHurdleDetails
                          ?.cumulativeRedemptions ?? 0
                    )}
                    dataTestId={`less-buybacks-cell-${instrumentId}-${trancheId}-${conditionIndex}-${caseItem.caseId}-${forecast.forecastId}`}
                  />
                )}
              />
            )}
            {hasToeDividends && (
              <TableSectionValuesPerForecast
                renderSpacer={false}
                title={l('_LessCumulativeDividends')}
                renderCell={({ caseItem, forecast }) => (
                  <CellValue
                    {...amountRules}
                    value={getWaterfallCaseYearInstrumentTrancheValue(
                      results,
                      caseItem.caseId,
                      forecast.forecastId,
                      instrumentId,
                      trancheId,
                      (tranche) =>
                        tranche.ordinaryEquityMetrics?.conditions[conditionIndex]?.toeHurdleDetails
                          ?.cumulativeDividends ?? 0
                    )}
                    dataTestId={`less-dividends-cell-${instrumentId}-${trancheId}-${conditionIndex}-${caseItem.caseId}-${forecast.forecastId}`}
                  />
                )}
              />
            )}
            {hasInterimDeductions && (
              <TableSectionValuesPerForecast
                renderSpacer={false}
                title={netConditionTitle}
                additionalCellClassNames="table-primary__cell--strong"
                additionalTitleClassNames="table-primary__cell--strong"
                renderCell={({ caseItem, forecast }) => (
                  <CellValue
                    {...amountRules}
                    value={getWaterfallCaseYearInstrumentTrancheValue(
                      results,
                      caseItem.caseId,
                      forecast.forecastId,
                      instrumentId,
                      trancheId,
                      (tranche) =>
                        tranche.ordinaryEquityMetrics?.conditions[conditionIndex]?.toeHurdleDetails
                          ?.finalHurdle ?? 0
                    )}
                    dataTestId={`net-toe-condition-cell-${instrumentId}-${trancheId}-${conditionIndex}-${caseItem.caseId}-${forecast.forecastId}`}
                  />
                )}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
};
